import React, { useState } from "react";

import AutocompleteSelectInput, {
  AutocompleteValue,
  createFilterOptions,
} from "veneer/AutocompleteSelectInput";
import ListItem from "veneer/ListItem";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";
import Typography from "veneer/Typography";

import { FormData } from "cortex/ui/src/fleet/view/device/model";
import SystemStrategyForm from "cortex/ui/src/fleet/view/strategy/component/SystemStrategyForm";
import { useAppSelector, useFeatureFlag } from "cortex/ui/src/hooks";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { isOperator } from "cortex/ui/src/redux-store/users/selectors";
import { OperationScheduleForm } from "cortex/ui/src/ui/operation-schedule";
import { type NumberOption, Option } from "cortex/ui/src/ui/table/types";
import ToggleSection from "cortex/ui/src/ui/toggle-section";

type TagAutocompleteOption = Option & {
  inputValue?: string;
};

const filter = createFilterOptions<TagAutocompleteOption>();

interface DeviceEditFormProps {
  applicationOptions: ReadonlyArray<NumberOption>;
  deviceTagOptions: ReadonlyArray<Option>;
  errors: {
    location?: string;
    name?: string;
  };
  networkOptions: ReadonlyArray<NumberOption>;
  onChange: (nextValue: DeviceEditFormProps["value"]) => void;
  readonly?: boolean;
  systemManaged?: boolean;
  value: FormData;
}

export default function DeviceEditForm({
  applicationOptions,
  deviceTagOptions,
  errors,
  networkOptions,
  onChange,
  readonly = false,
  systemManaged = false,
  value,
}: DeviceEditFormProps) {
  const isStrategiesEnabled = useFeatureFlag("is-strategies-enabled");
  const user = useAppSelector(getAuthUser);
  const userIsOperator = isOperator(user);
  const [inputValue, setInputValue] = useState("");
  const update = (patch: Partial<DeviceEditFormProps["value"]>) => {
    onChange({ ...value, ...patch });
  };
  const selectedNetworkId = value.selectedNetwork?.value;

  return (
    <>
      <Stack alignItems="start" direction="row" spacing={2}>
        <TextInput
          disabled={systemManaged}
          error={!!errors.name}
          fullWidth
          helperText="Required. Name your device for future reference."
          label="Device name"
          onChange={(name) => update({ name: name ?? undefined })}
          required
          value={value.name || ""}
        />
        <AutocompleteSelectInput
          disableClearable
          disabled={systemManaged}
          inputProps={{
            helperText:
              "Required. Please select the network this device will be part of.",
            label: "Network",
            required: true,
          }}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          onChange={(_e, opt) => update({ selectedNetwork: opt ?? null })}
          options={networkOptions}
          size="small"
          value={value.selectedNetwork as NumberOption | undefined}
        />
      </Stack>
      <Stack alignItems="start" direction="row" spacing={2}>
        <AutocompleteSelectInput<TagAutocompleteOption, true, false, true>
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const raw = params.inputValue.trim();
            const exists = options.some(
              (o) => o.label.toLowerCase() === raw.toLowerCase(),
            );
            if (raw && !exists) {
              filtered.push({ label: raw, value: raw });
            }
            return filtered;
          }}
          freeSolo
          getOptionLabel={(opts) =>
            typeof opts === "string" ? opts : opts.label
          }
          inputProps={{
            helperText: `Type one tag at a time and hit Enter. Tags are case-insensitive and have to start with alphanumeric characters. Tags can only contain alphanumeric characters, "-", "_" or a space.`,
            label: "Device Tags",
          }}
          inputValue={inputValue}
          isOptionEqualToValue={(opts, value) =>
            (typeof opts === "string" ? opts : opts.value) ===
            (typeof value === "string" ? value : value.value)
          }
          multiple
          onChange={(
            _,
            newValue: AutocompleteValue<
              TagAutocompleteOption,
              true,
              false,
              true
            >,
          ) => {
            // Normalize strings and "create" objects to {label, value}
            const normalized: Option[] = (newValue ?? []).map(
              (item: TagAutocompleteOption | string) => {
                if (typeof item === "string")
                  return { label: item, value: item };
                if (item?.inputValue)
                  return { label: item.inputValue, value: item.inputValue };
                return item as Option;
              },
            );
            update({ selectedTags: normalized });
          }}
          onInputChange={(_, newInput) => setInputValue(newInput)}
          options={deviceTagOptions}
          renderOption={(props, tag) => (
            <ListItem {...props} key={tag.value ?? "createAdvertiserOption"}>
              {tag.value !== tag.label ? tag.label : `Add tag: ${tag.label}`}
            </ListItem>
          )}
          size="small"
          value={value.selectedTags ? [...value.selectedTags] : []}
        />
        <TextInput
          error={!!errors.location}
          fullWidth
          helperText="GPS coordinates of this device. e.g. 39.965033,-75.179170"
          label="Device location"
          onChange={(location) => update({ location: location ?? undefined })}
          placeholder="latitude,longitude"
          value={value.location || ""}
        />
      </Stack>
      <ToggleSection
        checked={Boolean(value.testing)}
        checkedLabel="On"
        description="Checking this option will put the device in testing mode. When in
          testing mode, this device will get the latest testing versions of the
          applications. Non-testing mode devices cannot run the testing
          application versions."
        onChange={() => update({ testing: !value.testing })}
        title="Testing Device"
        uncheckedLabel="Off"
      ></ToggleSection>
      {!isStrategiesEnabled && (
        <ToggleSection
          checked={Boolean(value.hasSystemStrategy)}
          checkedLabel="On"
          description={
            <Typography variant="body2">
              Choose the application this device plays, and the fallback
              creative(s) it should play if the app provides none. When
              disabled, this device will use its{" "}
              <a
                href={`/networks/${selectedNetworkId}/edit`}
                rel="noopener noreferrer"
                target="_blank"
              >
                network defaults
              </a>
              .
            </Typography>
          }
          disabled={readonly || userIsOperator}
          onChange={() =>
            update({ hasSystemStrategy: !value.hasSystemStrategy })
          }
          title="Override Network's Content Source"
          uncheckedLabel="Off"
        >
          <SystemStrategyForm
            applicationOptions={applicationOptions}
            onChange={(nextValue) => {
              update({
                fallbackContents: nextValue.fallbackContents,
                selectedApplication: nextValue.selectedApplication,
              });
            }}
            readOnly={readonly || userIsOperator}
            type="device"
            value={{
              fallbackContents: value.fallbackContents,
              selectedApplication: value.selectedApplication,
            }}
          />
        </ToggleSection>
      )}
      <ToggleSection
        checked={Boolean(value.hasOverrideNetwork)}
        checkedLabel="On"
        description={
          <Typography variant="body2">
            The hours (according to your device's clock) this device plays ads.
            Outside these hours, your device shows a black screen. When
            disabled, this device will use its{" "}
            <a
              href={`/networks/${selectedNetworkId}/edit`}
              rel="noopener noreferrer"
              target="_blank"
            >
              network defaults
            </a>
            .
          </Typography>
        }
        onChange={() =>
          update({ hasOverrideNetwork: !value.hasOverrideNetwork })
        }
        title="Override Network's Screen Operating Hours"
        uncheckedLabel="Off"
      >
        <OperationScheduleForm
          onChange={(deviceOperateTime) =>
            update({ deviceOperateTime: deviceOperateTime ?? undefined })
          }
          readOnly={readonly || !value?.hasOverrideNetwork}
          schedule={value.deviceOperateTime ?? undefined}
        />
      </ToggleSection>
    </>
  );
}
